import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { InboundEmailDetailed, BusinessRef } from '@/types'
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal'
import { Mail, Paperclip, Eye, EyeOff, Download } from 'lucide-react'

type Props = {
  business: BusinessRef
  inbound_email: InboundEmailDetailed
}

export default function BackofficeBusinessInboundEmailsShow({ business, inbound_email }: Props) {
  function handleTogglePublish() {
    if (inbound_email.published) {
      router.delete(`/backoffice/businesses/${business.id}/inbound_emails/${inbound_email.id}/publication`)
    } else {
      router.post(`/backoffice/businesses/${business.id}/inbound_emails/${inbound_email.id}/publication`)
    }
  }

  return (
    <BackofficeLayout
      title={inbound_email.subject || '(no subject)'}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Inbound Emails', href: `/backoffice/businesses/${business.id}/inbound_emails` },
        { label: inbound_email.subject || '(no subject)' },
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <h1 className="text-xl font-semibold tracking-tight">
                {inbound_email.subject || '(no subject)'}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              From: <span className="font-mono">{inbound_email.from_address}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Received: {new Date(inbound_email.created_at).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {inbound_email.published ? (
              <Badge className="bg-green-600 text-white font-normal text-xs">Published</Badge>
            ) : (
              <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">Hidden</Badge>
            )}
            <Button size="sm" variant="outline" onClick={handleTogglePublish}>
              {inbound_email.published ? (
                <>
                  <EyeOff className="w-3.5 h-3.5 mr-1.5" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                  Publish
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Email Body */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Message</h2>
          </div>
          <div className="px-4 py-4">
            {inbound_email.body_html ? (
              <div
                className="prose prose-sm max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: inbound_email.body_html }}
              />
            ) : inbound_email.body_plain ? (
              <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">{inbound_email.body_plain}</pre>
            ) : (
              <p className="text-sm text-muted-foreground">(no message body)</p>
            )}
          </div>
        </section>

        {/* Attachments */}
        {inbound_email.attachments.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                Attachments ({inbound_email.attachments.length})
              </h2>
            </div>
            <div className="divide-y">
              {inbound_email.attachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{att.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {att.size < 1024 ? `${att.size} B` : att.size < 1048576 ? `${(att.size / 1024).toFixed(1)} KB` : `${(att.size / 1048576).toFixed(1)} MB`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <DocumentPreviewModal 
                      url={att.url} 
                      filename={att.name || 'Attachment'} 
                    />
                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost" title="Download">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </BackofficeLayout>
  )
}
